import { useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  DollarSign,
  Link2,
  Brain,
  Server,
  BarChart3,
  ShieldCheck,
  Activity,
  Database,
  Bot,
  UserCheck,
  AlertTriangle,
  Lock,
  Target,
  CheckCircle2,
  Ticket,
} from 'lucide-react'

const PLAN_PASSWORD = 'betadizee101'

type TaskType = 'ai' | 'manual'
type BuildStatus =
  | 'complete'
  | 'exists-needs-work'
  | 'partially-built'
  | 'not-built'

interface TaskItem {
  title: string
  description: string
  type: TaskType
}

interface DecisionItem {
  title: string
  description: string
}

interface Section {
  id: string
  number: number
  icon: typeof DollarSign
  title: string
  subtitle: string
  status: BuildStatus
  statusBanner: string
  color: string
  tasks: TaskItem[]
  decisions?: DecisionItem[]
}

const statusConfig: Record<BuildStatus, { label: string; bg: string; text: string; dot: string }> = {
  complete: { label: 'Complete', bg: 'bg-emerald-500/10', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  'exists-needs-work': { label: 'Exists — Needs Work', bg: 'bg-amber-500/10', text: 'text-amber-400', dot: 'bg-amber-400' },
  'partially-built': { label: 'Partially Built', bg: 'bg-sky-500/10', text: 'text-sky-400', dot: 'bg-sky-400' },
  'not-built': { label: 'Not Built', bg: 'bg-rose-500/10', text: 'text-rose-400', dot: 'bg-rose-400' },
}

const colorMap: Record<string, { border: string; bg: string; text: string; pill: string; bannerBg: string }> = {
  sky: {
    border: 'border-sky-500/20',
    bg: 'bg-sky-500/[0.04]',
    text: 'text-sky-400',
    pill: 'bg-sky-500/15 text-sky-300',
    bannerBg: 'bg-sky-500/[0.06] border-sky-500/15',
  },
  violet: {
    border: 'border-violet-500/20',
    bg: 'bg-violet-500/[0.04]',
    text: 'text-violet-400',
    pill: 'bg-violet-500/15 text-violet-300',
    bannerBg: 'bg-violet-500/[0.06] border-violet-500/15',
  },
  emerald: {
    border: 'border-emerald-500/20',
    bg: 'bg-emerald-500/[0.04]',
    text: 'text-emerald-400',
    pill: 'bg-emerald-500/15 text-emerald-300',
    bannerBg: 'bg-emerald-500/[0.06] border-emerald-500/15',
  },
  amber: {
    border: 'border-amber-500/20',
    bg: 'bg-amber-500/[0.04]',
    text: 'text-amber-400',
    pill: 'bg-amber-500/15 text-amber-300',
    bannerBg: 'bg-amber-500/[0.06] border-amber-500/15',
  },
  rose: {
    border: 'border-rose-500/20',
    bg: 'bg-rose-500/[0.04]',
    text: 'text-rose-400',
    pill: 'bg-rose-500/15 text-rose-300',
    bannerBg: 'bg-rose-500/[0.06] border-rose-500/15',
  },
  cyan: {
    border: 'border-cyan-500/20',
    bg: 'bg-cyan-500/[0.04]',
    text: 'text-cyan-400',
    pill: 'bg-cyan-500/15 text-cyan-300',
    bannerBg: 'bg-cyan-500/[0.06] border-cyan-500/15',
  },
}

/* ═══════════════════════════════════════════════════════════════════════════
   ALREADY-COMPLETE WORK (acknowledged, not in active plan)
   ═══════════════════════════════════════════════════════════════════════════ */
const completedWork = [
  {
    title: 'Consolidated Experience (Replaces Promoter / Manager Dashboards)',
    description:
      'All user types (artist, manager, support, promoter) share the same DIZEE experience. No separate dashboards. Users toggle on/off accounts in Team Hub, and every page (Shows, Music, Tasks, Money) already filters by active artist(s). Permission-based sidebar visibility gates which sections each role can see.',
    files: [
      'TeamHubPage.tsx — toggle on/off, request access, pending artists, promoter artist accounts, leave team',
      'TeamPage.tsx — My Accounts / Manage Team tabs',
      'Sidebar.tsx — dynamic visibility based on permissions + active artists',
      'useUserPermissions.ts — section-level gating per role',
      'ShowsPage / MusicPage / TasksPage / MoneyPage — filterArtistId dropdown, multi-artist fetch, dashboardArtistId',
    ],
  },
  {
    title: 'Multi-Team Filtering Across All Sections',
    description:
      'When a user is on multiple teams, every section loads data per active artist and renders a filter dropdown. Add forms include a team/artist selector. Data is tagged with dashboardArtistId for filtering.',
    files: [
      'api.ts — getActiveArtistIds(), toggleActiveArtistId(), setActiveArtistIds(), enterArtistContext()',
      'ShowsPage.tsx — Promise.all per artist, filterArtistId, permission-filtered shows',
      'MusicPage.tsx — same pattern, filterArtistId',
      'MoneyPage.tsx — same pattern, filterArtistId, formTeamArtistId',
      'TasksPage.tsx — filterArtistId',
    ],
  },
  {
    title: 'Activity System (Fully Built)',
    description:
      'Dual-write to localStorage + backend API. Tracks add/edit/delete/submit across shows, tasks, music, finance. Approve/deny flow for visibility changes (private → team). Progress bar. All sections wired in.',
    files: [
      'ActivityPage.tsx — feed, filters, approve/deny, progress',
      'activityApi.ts — backend client',
      'activityLog.ts — dual-write utility',
      'backend-dizee/src/Routes/activity.ts — list, create, progress, update status',
      'backend-dizee/src/controllers/activity.ts — full CRUD',
    ],
  },
  {
    title: 'Show → Money Guarantee Flow (Backend Cron + Frontend)',
    description:
      'Backend showFinancialsService runs at 4 AM PST. Marks past shows as completed, creates FinancialEntry from guarantees (revenue, paid, fromShows: true, bookingId). Frontend also generates virtual entries from bookings and de-duplicates against API entries. Both upcoming (unpaid) and past (paid) shows handled.',
    files: [
      'backend-dizee/src/services/showFinancialsService.ts — markPastShowsCompletedSimple(), ensureFinancialEntriesForCompletedShows(), upsertUnpaidShowEntry()',
      'backend-dizee/src/services/cronService.ts — scheduled run',
      'MoneyPage.tsx — fetchFromShowsEntries(), allEntries de-duplication',
    ],
  },
  {
    title: 'Backend Analytics Tracking (Custom)',
    description:
      'Session tracking, page views, interactions, scroll depth. Admin-only daily analytics reports and email reports. Not GA4 but covers in-app usage tracking.',
    files: [
      'backend-dizee/src/Routes/analyticsTracking.ts — session/start, session/end, heartbeat, track/app-usage, track/page-view, track/interaction, track/scroll-depth',
      'backend-dizee/src/controllers/analyticsController.ts — getDailyAnalytics, sendTestReport, sendDailyReport',
    ],
  },
  {
    title: 'Backend Admin Endpoints (Partial)',
    description:
      'Admin routes exist: db-seed, login, me, getUsers. Admin-only middleware (adminOnly) gates analytics and report endpoints. Foundation for a full admin dashboard.',
    files: [
      'backend-dizee/src/Routes/admin.ts — db-seed, login, me, users',
      'backend-dizee/src/middleware/adminAuth.ts — adminOnly middleware',
    ],
  },
  {
    title: 'Sync Status UI',
    description:
      'SyncStatusDot component renders synced/pending/out_of_sync states. Used on Shows, Tasks, Music, and Money pages. Data flows from backend syncStatus field.',
    files: [
      'SyncStatusDot.tsx — green/orange/red dot',
      'normalizeBookings.ts — maps syncStatus from API',
    ],
  },
  {
    title: 'Backend Team & Permissions Architecture',
    description:
      'Full team management: TeamMember model (artistId ↔ teamMemberId, role, permissions, sections, financialAccess), TeamInvitation, PromoterRequest, PendingArtist. Delegated access pattern (getDelegatedAccessContext) for multi-account. Financial access middleware (checkFinancialAccess). Promoter routes (shows, invite-artist).',
    files: [
      'backend-dizee/src/database/models/TeamMember.ts',
      'backend-dizee/src/database/models/TeamInvitation.ts',
      'backend-dizee/src/database/models/PromoterRequest.ts',
      'backend-dizee/src/database/models/PendingArtist.ts',
      'backend-dizee/src/helper/delegatedAccess.ts',
      'backend-dizee/src/middleware/financialFilter.ts',
      'backend-dizee/src/Routes/promoter.ts',
    ],
  },
]

/* ═══════════════════════════════════════════════════════════════════════════
   REMAINING WORK — SECTION DATA
   ═══════════════════════════════════════════════════════════════════════════ */
const sections: Section[] = [
  /* ── 1 — DIZEE Links ───────────────────────────────────────────────── */
  {
    id: 'links',
    number: 1,
    icon: Link2,
    title: 'DIZEE Links',
    subtitle: 'Shareable links for data collection, releases, and future link types',
    status: 'exists-needs-work',
    statusBanner:
      'LinksPage is fully built with create flow, 4 summary boxes (Total Links, Active, Engagement, Pre-saves), spreadsheet table, and two link types: Data Collection and Release. Public pages work at /l/collect/:linkId and /l/release/:slug. CollectLinkPage has all planned fields (legal name, release date, splits, master/publishing, artwork, label copy). HOWEVER: Links is hidden in the sidebar (filtered out in Sidebar.tsx). All data is localStorage only — no backend persistence. LinkDetailPage exists for viewing submissions.',
    color: 'emerald',
    tasks: [
      {
        title: 'Unhide Links in the sidebar',
        description:
          'In Sidebar.tsx, Links is explicitly filtered out with: if (item.href === \'/links\') return false. Remove this filter to make Links visible in the navigation.',
        type: 'ai',
      },
      {
        title: 'Add backend persistence for links',
        description:
          'Currently linksStore.ts stores everything in localStorage. Build API endpoints to persist links and submissions server-side. Keep localStorage as a cache/fallback but make the backend the source of truth. Needs a Links model in backend-dizee.',
        type: 'ai',
      },
      {
        title: 'Auto-create upcoming release on data collection submission',
        description:
          'When a data collection form is submitted via CollectLinkPage, create a corresponding release entry in Music with the collected metadata. This bridges Links → Music.',
        type: 'ai',
      },
      {
        title: 'Wire pre-save and click data to backend',
        description:
          'When fans interact with release links (pre-saves, platform clicks), store engagement events in the backend. Currently clickCount/submissionCount/preSaveCount exist on the link object but only in localStorage.',
        type: 'ai',
      },
      {
        title: 'Test links end-to-end: create → share URL → submit form → verify data',
        description:
          'Create a data collection link and a release link. Open the public URLs. Submit data through the collection form. Open the release link and test pre-save. Verify all data flows back.',
        type: 'manual',
      },
    ],
    decisions: [
      {
        title: 'Backend for links: extend external API or local Express backend?',
        description:
          'Links currently use localStorage. Should we add link endpoints to backend-dizee (which already handles bookings, catalog, financials, team, activity) or build a separate service?',
      },
    ],
  },

  /* ── 2 — Money Section Enhancements ────────────────────────────────── */
  {
    id: 'money',
    number: 2,
    icon: DollarSign,
    title: 'Money Section Enhancements',
    subtitle: 'Summary boxes, time-range filters, status types, attachments, running balance',
    status: 'exists-needs-work',
    statusBanner:
      'MoneyPage is fully built (~1400 lines) with CRUD, paid/unpaid toggling, search/filter by type and category, bulk delete, multi-artist filtering, and show guarantee entries flowing in via fetchFromShowsEntries + backend cron. GAPS: Summary boxes are Week/Month/YTD instead of Income/Expenses/Projected Net/Cash Flow. No time-range filters (7/14/30/90 days). Only revenue/spend types — no Planned/Invoiced/Past Due/Paid/Received statuses. No attachment support. No commission entries. No running balance column.',
    color: 'emerald',
    tasks: [
      {
        title: 'Replace summary boxes with plan layout',
        description:
          'Replace current Week/Month/YTD summary boxes with: Income, Expenses, Projected Net, Cash Flow. These should update dynamically based on the selected timeframe filter.',
        type: 'ai',
      },
      {
        title: 'Add time-range filter bar',
        description:
          'Add filter options: All, Next 7 days, Next 14 days, Next 30 days, Next 90 days, Next 6 months, Next 12 months. Control which transactions are visible and update the summary boxes.',
        type: 'ai',
      },
      {
        title: 'Add transaction status types',
        description:
          'Replace the is_paid boolean with full status types: Planned, Invoiced, Past Due, Paid, Received. Add status column to spreadsheet and status filter. Backend FinancialEntry model already stores type/isPaid but needs a new status field.',
        type: 'ai',
      },
      {
        title: 'Add attachment support (links only, no file upload)',
        description:
          'Each transaction should allow attaching documentation links: PDF URL, Google Drive, Dropbox, Notion, or general external link. No file upload — just link fields. Lightweight approach.',
        type: 'ai',
      },
      {
        title: 'Add running balance column',
        description:
          'Add a Running Balance column that calculates cumulatively based on sorted transactions (income adds, expenses subtract).',
        type: 'ai',
      },
      {
        title: 'Add commission entry type',
        description:
          'Add a "Commissions" action alongside Add Income and Add Expense. Commissions should eventually auto-calculate from show commission rules.',
        type: 'ai',
      },
      {
        title: 'Review Money page with real financial data',
        description:
          'After enhancements, load real financial entries and verify: summary boxes calculate correctly, filters work, statuses display, attachments save, running balance is accurate.',
        type: 'manual',
      },
    ],
  },

  /* ── 3 — Money ↔ Shows Financial Architecture ─────────────────────── */
  {
    id: 'money-shows',
    number: 3,
    icon: DollarSign,
    title: 'Money ↔ Shows: Beyond Guarantees',
    subtitle: 'Extend show-to-Money flow for bonuses, commissions, expenses, payment schedules',
    status: 'partially-built',
    statusBanner:
      'The guarantee → Money pipeline is solid: backend cron (showFinancialsService) creates/updates FinancialEntry records, frontend de-duplicates virtual vs API entries. Show model in the backend already has financial fields: guarantee, walkout_potential, gross_potential, net_potential, artist_net, artist_profit, split_point, amount_to_split, deal_type, deal_info, deposit_due, deposit_received, total_fees, currency_conversion. ShowsPage form exposes these as basic + advanced fields. GAPS: Only guarantee flows to Money. Bonuses, commissions, travel, walkout potential don\'t create derived transactions. No payment schedule support (deposit + settlement as separate entries).',
    color: 'amber',
    tasks: [
      {
        title: 'Extend showFinancialsService to create entries from more fields',
        description:
          'Currently only guarantee creates a FinancialEntry. Extend to also create entries from: walkout_potential (projected income), deposit_due (expense if outbound), deposit_received (income). Map each to the correct type and category. The backend Bookings model already has these fields.',
        type: 'ai',
      },
      {
        title: 'Add bonus and commission fields to the show form',
        description:
          'The backend Bookings model doesn\'t currently have bonus or commission fields. Add these to the model and to the ShowsPage add/edit form. Then extend showFinancialsService to create derived Money entries from them.',
        type: 'ai',
      },
      {
        title: 'Build payment schedule support',
        description:
          'Shows often have multiple payments (deposit, settlement, bonus payout). Add a payment schedule array to the Bookings model. Each payment schedule item should generate its own Money transaction with the correct due date for accurate cash flow projections.',
        type: 'ai',
      },
      {
        title: 'Verify show-to-Money flow with real shows',
        description:
          'Create shows with guarantees, bonuses, commissions, and multi-payment schedules. Verify each creates the correct Money entries with correct dates, amounts, and types.',
        type: 'manual',
      },
    ],
  },

  /* ── 4 — Ticket Count History ──────────────────────────────────────── */
  {
    id: 'ticket-history',
    number: 4,
    icon: Ticket,
    title: 'Ticket Count History & Pacing',
    subtitle: 'Historical ticket count logging for pacing analysis and intelligence',
    status: 'not-built',
    statusBanner:
      'Shows have a ticket counter (tix_sold field) and the form supports entering ticket counts. However, every update overwrites the previous value — there is no historical log of ticket count changes over time. The plan requires every ticket count update to log: ticket value, date, time, submitting user. This builds the pacing dataset the intelligence layer will use.',
    color: 'rose',
    tasks: [
      {
        title: 'Create TicketHistory model in backend',
        description:
          'Create a TicketHistory collection that stores every ticket count update: bookingId, ticketCount, previousCount, timestamp, submittingUserId, submittingUserEmail. Index on bookingId + timestamp for efficient pacing queries.',
        type: 'ai',
      },
      {
        title: 'Add ticket history API endpoints',
        description:
          'Add endpoints: POST to create a ticket history entry (called whenever tix_sold is updated), GET to retrieve ticket history for a booking (for pacing charts).',
        type: 'ai',
      },
      {
        title: 'Wire ticket count updates to create history entries',
        description:
          'In the ShowsPage ticket counter update flow and the backend booking update endpoint, create a TicketHistory entry every time tix_sold changes. Include the previous value for delta calculation.',
        type: 'ai',
      },
      {
        title: 'Build ticket pacing visualization',
        description:
          'Add a pacing chart/view to the show detail or dropdown that displays ticket count over time. This is the foundation for the intelligence layer\'s pacing analysis.',
        type: 'ai',
      },
      {
        title: 'Test ticket history with real show data',
        description:
          'Update ticket counts on a show multiple times over several days. Verify the history log captures every change and the pacing visualization renders correctly.',
        type: 'manual',
      },
    ],
  },

  /* ── 5 — Intelligence System ───────────────────────────────────────── */
  {
    id: 'intelligence',
    number: 5,
    icon: Brain,
    title: 'Intelligence System',
    subtitle: 'Intelligent views for Shows, Music, and Money — insights from account data',
    status: 'not-built',
    statusBanner:
      'No intelligence layer exists. The Activity system provides a feed of events but no analysis or insights. The plan calls for an "Intelligent View" toggle inside Shows, Music, and Money that surfaces signals like: shows at risk, slow ticket pacing, market comparisons, cash flow warnings, financial projections. Depends on historical data from ticket history, financial transactions, and sync states. Budget: $750 (Richard\'s brother).',
    color: 'rose',
    tasks: [
      {
        title: 'Design intelligence signal definitions and thresholds',
        description:
          'Define what constitutes "at risk", "slow pacing", "cash flow warning". Define the input data each signal requires and the thresholds for triggering. This design doc informs both backend logic and UI.',
        type: 'ai',
      },
      {
        title: 'Build "Intelligent View" toggle component',
        description:
          'Create a reusable component for Shows, Music, and Money pages. When toggled on, it overlays insight cards on the default view. Suggested actions: create a task, notify team, review data.',
        type: 'ai',
      },
      {
        title: 'Implement Shows intelligence: at-risk, pacing analysis',
        description:
          'Using ticket history data: calculate current vs expected pacing, flag shows at risk. Display historical performance in that market. Requires Ticket History (Section 4) to be built first.',
        type: 'ai',
      },
      {
        title: 'Implement Money intelligence: cash flow warnings, projections',
        description:
          'Analyze upcoming transactions: cash flow risk (expenses > income), unpaid invoices, commissions owed, high expenses. Example: "Expenses exceed projected income in the next 30 days."',
        type: 'ai',
      },
      {
        title: 'Implement Music intelligence: streaming trends',
        description:
          'Using streaming data from Soundcharts: declining streams, underperforming releases, upcoming releases needing promotion.',
        type: 'ai',
      },
      {
        title: 'Review intelligence signals with real data',
        description:
          'Validate that signals make sense, pacing calculations are correct, cash flow projections are accurate. Tune thresholds.',
        type: 'manual',
      },
    ],
    decisions: [
      {
        title: 'Budget scope: what does $750 cover?',
        description:
          'Is the $750 for backend logic only, full-stack, or just the UI? If backend only, AI can build the frontend.',
      },
      {
        title: 'Client-side or server-side intelligence?',
        description:
          'Compute signals on the frontend (using loaded data) or backend (pre-computed insights via API)? Client-side ships faster; server-side scales better.',
      },
    ],
  },

  /* ── 6 — Production Architecture (EDDIE) ───────────────────────────── */
  {
    id: 'production',
    number: 6,
    icon: Server,
    title: 'Production Architecture',
    subtitle: 'Staging environment, deployment pipeline, environment separation',
    status: 'not-built',
    statusBanner:
      'No staging environment exists. The app runs in a single environment. No deployment pipeline (Dev → Staging → QA → Production). This is infrastructure work.',
    color: 'rose',
    tasks: [
      {
        title: 'Set up staging environment (separate app + database)',
        description:
          'Create a staging deployment with its own database. Structure: production app + production database, staging app + staging database.',
        type: 'manual',
      },
      {
        title: 'Configure deployment pipeline: Dev → Staging → QA → Production',
        description:
          'Set up CI/CD so staging branch auto-deploys, and production requires manual promotion after QA. GitHub Actions, Vercel previews, or similar.',
        type: 'manual',
      },
      {
        title: 'Add environment variable separation',
        description:
          'Ensure .env.staging and .env.production are properly configured with separate API URLs, database connections, and feature flags.',
        type: 'manual',
      },
      {
        title: 'Add environment-aware config utility',
        description:
          'Create a utility that reads VITE_ENV to determine the current environment. Useful for staging banners, feature flags, and API pointing.',
        type: 'ai',
      },
    ],
  },

  /* ── 7 — GA4 Analytics (EDDIE) ─────────────────────────────────────── */
  {
    id: 'analytics',
    number: 7,
    icon: BarChart3,
    title: 'GA4 Analytics',
    subtitle: 'Google Analytics on top of existing custom analytics tracking',
    status: 'partially-built',
    statusBanner:
      'The backend already has custom analytics tracking: session start/end/heartbeat, app usage, page views, interactions, scroll depth (analyticsTracking routes + AnalyticsController). Admin-only daily analytics reports exist. HOWEVER: no GA4/gtag.js in the frontend. The plan calls for GA4 specifically for: signups, invites, shows created, ticket updates, link creation, link clicks, money entries. GA4 would complement the existing custom tracking with Google\'s dashboard, audiences, and conversion tracking.',
    color: 'amber',
    tasks: [
      {
        title: 'Set up GA4 property and get measurement ID',
        description:
          'Create a GA4 property in Google Analytics. Get the measurement ID (G-XXXXXXXXXX). Decide: one property for staging + production or separate?',
        type: 'manual',
      },
      {
        title: 'Install GA4 gtag.js and create analytics utility',
        description:
          'Add the GA4 script to index.html. Create src/lib/analytics.ts with typed helpers: trackSignup(), trackInviteSent(), trackShowCreated(), trackTicketUpdate(), trackLinkCreated(), trackLinkClick(), trackMoneyEntry(). These complement the existing custom tracking.',
        type: 'ai',
      },
      {
        title: 'Wire GA4 events into existing flows',
        description:
          'Many flows already have logActivity() calls. Add GA4 tracking alongside them at key points: signup, invite, show CRUD, ticket counter, link creation, link page interactions, Money add/edit.',
        type: 'ai',
      },
      {
        title: 'Verify events in GA4 real-time dashboard',
        description:
          'Perform key actions and verify events appear in GA4 real-time. Confirm event names and parameters.',
        type: 'manual',
      },
    ],
  },

  /* ── 8 — Admin Dashboard (EDDIE) ──────────────────────────────────── */
  {
    id: 'admin',
    number: 8,
    icon: ShieldCheck,
    title: 'Secure Internal Admin Dashboard',
    subtitle: 'Frontend admin panel powered by existing backend admin endpoints',
    status: 'partially-built',
    statusBanner:
      'Backend admin routes already exist: /api/v1/admin (db-seed, login, me, getUsers). The adminOnly middleware gates analytics endpoints. HOWEVER: there is no admin frontend — no /admin page in the React app. The plan requires: view users/accounts, inspect show sync states, view ticket pacing history, view financial entries, debug account issues. Security: admin role, email whitelist, 2FA.',
    color: 'amber',
    tasks: [
      {
        title: 'Build admin page shell with auth gate',
        description:
          'Create /admin route with AdminPage component. Use the existing backend admin/login endpoint for authentication. Add admin role check and email whitelist.',
        type: 'ai',
      },
      {
        title: 'Build admin views: users, shows, financials, sync states',
        description:
          'Add admin views to browse: all users (with search), all shows (with sync state), ticket pacing history, financial entries, team relationships. Read-only inspection with search/filter. Backend getUsers endpoint exists; may need additional admin endpoints for shows/financials.',
        type: 'ai',
      },
      {
        title: 'Add additional admin API endpoints',
        description:
          'Extend backend-dizee /api/v1/admin with: list all shows (across accounts), list all financial entries, get sync state for a show, get team members for a user. These power the admin views.',
        type: 'ai',
      },
      {
        title: 'Set up admin email whitelist and 2FA',
        description:
          'Define the admin email whitelist. Implement 2FA for admin users — TOTP (Google Authenticator) or email OTP.',
        type: 'manual',
      },
    ],
  },

  /* ── 9 — System Monitoring (EDDIE) ────────────────────────────────── */
  {
    id: 'monitoring',
    number: 9,
    icon: Activity,
    title: 'System Monitoring',
    subtitle: 'Error logging, API logs, failed request alerts',
    status: 'not-built',
    statusBanner:
      'No system monitoring exists. Need: runtime error logging, backend job monitoring, API request/response logs, failed request alerts.',
    color: 'rose',
    tasks: [
      {
        title: 'Set up error tracking (Sentry or similar)',
        description:
          'Set up Sentry for frontend and backend. Configure source maps. Set up alert rules.',
        type: 'manual',
      },
      {
        title: 'Add frontend error boundary with reporting',
        description:
          'Create a React error boundary that catches rendering errors, reports to monitoring service, and shows a user-friendly error screen.',
        type: 'ai',
      },
      {
        title: 'Add API request logging middleware',
        description:
          'Add Express middleware to log: method, path, status code, response time, errors. Structured log format.',
        type: 'manual',
      },
      {
        title: 'Set up failed request alerts',
        description:
          'Configure alerts (Slack, email) for: 5xx spikes, slow APIs, failed background jobs.',
        type: 'manual',
      },
    ],
  },

  /* ── 10 — Data Architecture for Intelligence ───────────────────────── */
  {
    id: 'data-architecture',
    number: 10,
    icon: Database,
    title: 'Data Architecture for Intelligence',
    subtitle: 'Historical records that power the intelligence layer',
    status: 'partially-built',
    statusBanner:
      'The Activity model stores event history (eventType, section, actor, target, status, timestamps). This covers activity-level audit trail. GAPS: No dedicated ticket count history (covered in Section 4). No financial transaction change log (edits, status changes). No link usage event records (individual clicks/pre-saves). These datasets are needed for the intelligence layer.',
    color: 'amber',
    tasks: [
      {
        title: 'Build financial transaction change log',
        description:
          'Store historical snapshots when financial entries are edited or change status. Allows the intelligence layer to analyze trends and detect anomalies.',
        type: 'ai',
      },
      {
        title: 'Build link usage event tracking',
        description:
          'Store individual link interaction events: clicks, pre-saves, form submissions, timestamps, referrer data. Backend-side — not just aggregate counts.',
        type: 'ai',
      },
      {
        title: 'Set up database indexes and retention policies',
        description:
          'Ensure historical data tables have proper indexes for intelligence query patterns. Define retention policies.',
        type: 'manual',
      },
    ],
  },
]

/* ═══════════════════════════════════════════════════════════════════════════
   EXECUTION ORDER
   ═══════════════════════════════════════════════════════════════════════════ */
interface Phase {
  number: number
  title: string
  subtitle: string
  color: string
  items: string[]
}

const phases: Phase[] = [
  {
    number: 1,
    title: 'Quick Wins — Ship What\'s Ready',
    subtitle: 'One-line changes and light wiring to unlock existing features',
    color: 'emerald',
    items: [
      'Unhide Links in the sidebar (one-line change in Sidebar.tsx)',
      'Add environment-aware config utility for staging/prod',
      'Install GA4 gtag.js and create analytics utility',
    ],
  },
  {
    number: 2,
    title: 'Money Enhancements + Links Backend',
    subtitle: 'Upgrade Money section and give Links a real backend',
    color: 'sky',
    items: [
      'Replace Money summary boxes (Income / Expenses / Projected Net / Cash Flow)',
      'Add time-range filters and transaction status types to Money',
      'Add attachment support, running balance, commission entries to Money',
      'Add backend persistence for DIZEE Links (replace localStorage)',
      'Auto-create release on data collection submission',
    ],
  },
  {
    number: 3,
    title: 'Show → Money Expansion + Ticket History',
    subtitle: 'Extend the financial pipeline and build pacing data',
    color: 'violet',
    items: [
      'Extend showFinancialsService: bonuses, commissions, walkout, deposits',
      'Add bonus/commission fields to Bookings model and ShowsPage form',
      'Build payment schedule support (deposit + settlement + bonus payouts)',
      'Create TicketHistory model and wire ticket count updates to it',
      'Build ticket pacing visualization',
    ],
  },
  {
    number: 4,
    title: 'Intelligence Layer',
    subtitle: 'Insights powered by historical data from Phases 2-3',
    color: 'amber',
    items: [
      'Design intelligence signal definitions and thresholds',
      'Build "Intelligent View" toggle for Shows, Music, Money',
      'Shows intelligence: at-risk flags, pacing analysis',
      'Money intelligence: cash flow warnings, projections',
      'Music intelligence: streaming trends',
    ],
  },
  {
    number: 5,
    title: 'EDDIE — Infrastructure (Parallel)',
    subtitle: 'Run alongside product work — staging, GA4, admin, monitoring',
    color: 'rose',
    items: [
      'Set up staging environment + deployment pipeline',
      'Set up GA4 property, wire events, verify in real-time',
      'Build admin dashboard frontend (backend endpoints exist)',
      'Set up Sentry, API logging, failed request alerts',
      'Admin email whitelist + 2FA',
    ],
  },
]

/* ═══════════════════════════════════════════════════════════════════════════
   END-OF-MONTH OUTCOMES
   ═══════════════════════════════════════════════════════════════════════════ */
const outcomes = [
  { label: 'Consolidated experience for all roles (artist, manager, promoter, support)', status: 'complete' as BuildStatus },
  { label: 'Multi-team filtering across Shows, Music, Tasks, Money', status: 'complete' as BuildStatus },
  { label: 'Team Hub with toggle on/off, request access, pending artists', status: 'complete' as BuildStatus },
  { label: 'Activity system with approve/deny and progress tracking', status: 'complete' as BuildStatus },
  { label: 'Show guarantee → Money flow (backend cron + frontend)', status: 'complete' as BuildStatus },
  { label: 'DIZEE Links system (visible, backend-persisted)', status: 'exists-needs-work' as BuildStatus },
  { label: 'Money enhancements (summary boxes, filters, statuses, attachments)', status: 'exists-needs-work' as BuildStatus },
  { label: 'Show → Money: bonuses, commissions, payment schedules', status: 'partially-built' as BuildStatus },
  { label: 'Historical ticket pacing data', status: 'not-built' as BuildStatus },
  { label: 'Intelligence layer across Shows, Music, and Money', status: 'not-built' as BuildStatus },
  { label: 'Production infrastructure (staging, GA4, admin, monitoring)', status: 'partially-built' as BuildStatus },
]

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
export default function PlanMarchPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())
  const [showCompleted, setShowCompleted] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === PLAN_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const expandAll = () => setOpenSections(new Set(sections.map((s) => s.id)))
  const collapseAll = () => setOpenSections(new Set())

  const totalSections = sections.length
  const aiBuildTasks = sections.reduce((sum, s) => sum + s.tasks.filter((t) => t.type === 'ai').length, 0)
  const manualTasks = sections.reduce((sum, s) => sum + s.tasks.filter((t) => t.type === 'manual').length, 0)
  const totalDecisions = sections.reduce((sum, s) => sum + (s.decisions?.length || 0), 0)
  const notBuiltCount = sections.filter((s) => s.status === 'not-built').length

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8 bg-zinc-950">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
              <span className="text-white text-lg font-semibold tracking-tight">DIZEE</span>
              <span className="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider bg-white/10 text-zinc-400 rounded">March Plan</span>
            </div>
            <p className="text-[13px] text-zinc-500">Enter the password to view the March development plan.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-[12px] text-zinc-400 mb-2 tracking-wide uppercase">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Enter password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent transition-all"
              />
              {error && (
                <p className="mt-2 text-[11px] text-rose-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-6 py-3 text-sm font-medium text-zinc-950 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
            >
              View Plan
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.015] rotate-[-25deg]">
          <span className="text-[180px] font-bold tracking-widest text-white whitespace-nowrap select-none">
            CONFIDENTIAL
          </span>
        </div>
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-zinc-950/80 border-b border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
            <span className="text-white text-base font-semibold tracking-tight">DIZEE</span>
            <span className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-white/[0.08] text-zinc-300 rounded-full">
              March Plan
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-zinc-600 uppercase tracking-wider">Confidential</span>
            <Lock className="w-3.5 h-3.5 text-zinc-600" />
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-10 space-y-12">
        <div>
          <h1 className="text-2xl font-bold text-white mb-3">March Development Plan</h1>
          <p className="text-[13px] text-zinc-400 leading-relaxed max-w-3xl">
            Audited against both frontend (dizee) and backend (backend-dizee) codebases as of March 8, 2026. The promoter/manager dashboard direction has changed — everything is now consolidated into one DIZEE experience with permission-based filtering. This plan reflects only what remains to be built.
          </p>
        </div>

        {/* ── Already Completed (Collapsible) ─────────────────────────────── */}
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] overflow-hidden">
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            className="w-full flex items-center gap-4 px-6 py-5 text-left group"
          >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-emerald-500/15 text-emerald-300">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h2 className="text-[15px] font-semibold text-white">Already Completed ({completedWork.length} items)</h2>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Done
                </span>
              </div>
              <p className="text-[12px] text-zinc-500 mt-1">
                Consolidated experience, multi-team filtering, activity system, show → Money flow, analytics tracking, admin endpoints, sync status, team architecture
              </p>
            </div>
            <div className="flex-shrink-0 text-zinc-600 group-hover:text-zinc-400 transition-colors">
              {showCompleted ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </div>
          </button>

          {showCompleted && (
            <div className="px-6 pb-6 space-y-4">
              {completedWork.map((item, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-emerald-500/10 bg-emerald-500/[0.03] p-4"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <h4 className="text-[13px] font-medium text-white">{item.title}</h4>
                  </div>
                  <p className="text-[12px] text-zinc-500 leading-relaxed mb-3 ml-6">{item.description}</p>
                  <div className="ml-6 space-y-1">
                    {item.files.map((file, j) => (
                      <div key={j} className="text-[11px] text-zinc-600 font-mono flex items-start gap-2">
                        <span className="text-emerald-500/60 mt-0.5">-</span>
                        <span>{file}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Quick Nav Pills ──────────────────────────────────────────────── */}
        <div>
          <h2 className="text-[12px] text-zinc-500 uppercase tracking-wider font-semibold mb-3">Remaining Work</h2>
          <nav className="flex flex-wrap gap-2">
            {sections.map((s) => {
              const c = colorMap[s.color] || colorMap.sky
              return (
                <button
                  key={s.id}
                  onClick={() => {
                    if (!openSections.has(s.id)) toggleSection(s.id)
                    document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-medium border transition-all hover:scale-[1.03] ${c.border} ${c.bg} ${c.text}`}
                >
                  {s.number}. {s.title}
                </button>
              )
            })}
          </nav>
        </div>

        {/* ── Progress Overview Cards ──────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Remaining Sections', value: totalSections, color: 'text-white' },
            { label: 'AI Tasks', value: aiBuildTasks, color: 'text-sky-400' },
            { label: 'Eddie Tasks', value: manualTasks, color: 'text-violet-400' },
            { label: 'Decisions Needed', value: totalDecisions, color: 'text-amber-400' },
            { label: 'Not Yet Built', value: notBuiltCount, color: 'text-rose-400' },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5"
            >
              <div className="text-[11px] text-zinc-500 uppercase tracking-wider mb-2">{card.label}</div>
              <div className={`text-2xl font-semibold ${card.color}`}>{card.value}</div>
            </div>
          ))}
        </div>

        {/* ── Expand / Collapse All ────────────────────────────────────────── */}
        <div className="flex items-center gap-3">
          <button
            onClick={expandAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Expand All
          </button>
          <span className="text-zinc-700">|</span>
          <button
            onClick={collapseAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Collapse All
          </button>
        </div>

        {/* ── Sections ─────────────────────────────────────────────────────── */}
        <div className="space-y-4">
          {sections.map((section) => {
            const isOpen = openSections.has(section.id)
            const c = colorMap[section.color] || colorMap.sky
            const st = statusConfig[section.status]
            const Icon = section.icon

            return (
              <div
                key={section.id}
                id={`section-${section.id}`}
                className={`rounded-xl border ${c.border} ${c.bg} overflow-hidden transition-all scroll-mt-24`}
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-4 px-6 py-5 text-left group"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${c.pill}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] text-zinc-600 font-medium">#{section.number}</span>
                      <h2 className="text-[15px] font-semibold text-white truncate">{section.title}</h2>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${st.bg} ${st.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                        {st.label}
                      </span>
                    </div>
                    <p className="text-[12px] text-zinc-500 mt-1 truncate">{section.subtitle}</p>
                  </div>
                  <div className="flex-shrink-0 text-zinc-600 group-hover:text-zinc-400 transition-colors">
                    {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-6 pb-6 space-y-5">
                    <div className={`rounded-lg border p-4 ${c.bannerBg}`}>
                      <p className="text-[12px] text-zinc-400 leading-relaxed">{section.statusBanner}</p>
                    </div>

                    {section.decisions && section.decisions.length > 0 && (
                      <div className="space-y-3">
                        {section.decisions.map((d, i) => (
                          <div
                            key={i}
                            className="rounded-lg border border-amber-500/20 bg-amber-500/[0.06] p-4"
                          >
                            <div className="flex items-start gap-3">
                              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                              <div>
                                <h4 className="text-[13px] font-semibold text-amber-300">{d.title}</h4>
                                <p className="text-[12px] text-zinc-400 mt-1 leading-relaxed">{d.description}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="space-y-3">
                      {section.tasks.filter((t) => t.type === 'ai').length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Bot className="w-3.5 h-3.5 text-sky-400" />
                            <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                              Things I Can Build
                            </span>
                          </div>
                          <div className="space-y-2">
                            {section.tasks
                              .filter((t) => t.type === 'ai')
                              .map((task, i) => (
                                <div
                                  key={i}
                                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-start gap-3"
                                >
                                  <Bot className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="text-[13px] font-medium text-white">{task.title}</h4>
                                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-500/15 text-sky-300">
                                        AI Build
                                      </span>
                                    </div>
                                    <p className="text-[12px] text-zinc-500 mt-1.5 leading-relaxed">{task.description}</p>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {section.tasks.filter((t) => t.type === 'manual').length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3 mt-4">
                            <UserCheck className="w-3.5 h-3.5 text-violet-400" />
                            <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                              Things Eddie Needs to Do
                            </span>
                          </div>
                          <div className="space-y-2">
                            {section.tasks
                              .filter((t) => t.type === 'manual')
                              .map((task, i) => (
                                <div
                                  key={i}
                                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-start gap-3"
                                >
                                  <UserCheck className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="text-[13px] font-medium text-white">{task.title}</h4>
                                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-500/15 text-violet-300">
                                        Eddie
                                      </span>
                                    </div>
                                    <p className="text-[12px] text-zinc-500 mt-1.5 leading-relaxed">{task.description}</p>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* ── Execution Order ──────────────────────────────────────────────── */}
        <div className="pt-8">
          <h2 className="text-lg font-semibold text-white mb-2">Recommended Execution Order</h2>
          <p className="text-[12px] text-zinc-500 mb-8">
            Phases 1–4 are product development (AI + Richard). Phase 5 is infrastructure (Eddie) and runs in parallel.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {phases.map((phase) => {
              const c = colorMap[phase.color] || colorMap.sky
              return (
                <div
                  key={phase.number}
                  className={`rounded-xl border ${c.border} ${c.bg} p-6`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${c.pill}`}>
                      {phase.number}
                    </span>
                    <div>
                      <h3 className="text-[14px] font-semibold text-white">{phase.title}</h3>
                      <p className="text-[11px] text-zinc-500">{phase.subtitle}</p>
                    </div>
                  </div>
                  <ul className="space-y-2.5">
                    {phase.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${c.text.replace('text-', 'bg-')}`} />
                        <span className="text-[12px] text-zinc-400 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>
        </div>

        {/* ── End-of-Month Outcomes ────────────────────────────────────────── */}
        <div className="pt-8">
          <h2 className="text-lg font-semibold text-white mb-2">End-of-Month Outcomes</h2>
          <p className="text-[12px] text-zinc-500 mb-6">
            Each outcome mapped against current build status.
          </p>
          <div className="space-y-3">
            {outcomes.map((outcome, i) => {
              const st = statusConfig[outcome.status]
              return (
                <div
                  key={i}
                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <Target className="w-4 h-4 text-zinc-500" />
                    <span className="text-[13px] text-zinc-300">{outcome.label}</span>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${st.bg} ${st.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                    {st.label}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* ── Codebase Audit ───────────────────────────────────────────────── */}
        <div className="pt-8">
          <h2 className="text-lg font-semibold text-white mb-2">Codebase Audit Summary</h2>
          <p className="text-[12px] text-zinc-500 mb-6">
            What the original plan called for vs. where things actually stand after your recent work.
          </p>
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left px-5 py-3 text-zinc-500 font-medium uppercase tracking-wider">Original Plan Item</th>
                  <th className="text-left px-5 py-3 text-zinc-500 font-medium uppercase tracking-wider">Actual Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {[
                  { feature: 'Promoter Dashboard (separate page)', status: 'Replaced — consolidated experience', done: true },
                  { feature: 'Manager Dashboard (separate page)', status: 'Replaced — consolidated experience', done: true },
                  { feature: 'Promoter ↔ Artist Sync System', status: 'Replaced — toggle on/off in Team Hub', done: true },
                  { feature: 'Multi-team filtering', status: 'Complete — Shows, Music, Tasks, Money', done: true },
                  { feature: 'Activity system', status: 'Complete — dual-write, approve/deny, progress', done: true },
                  { feature: 'Show guarantee → Money', status: 'Complete — backend cron + frontend', done: true },
                  { feature: 'Backend analytics tracking', status: 'Complete — sessions, page views, interactions', done: true },
                  { feature: 'Backend admin endpoints', status: 'Partial — login, users (need more)', done: false },
                  { feature: 'DIZEE Links', status: 'Built, hidden sidebar, localStorage only', done: false },
                  { feature: 'Money enhancements', status: 'Needs summary boxes, filters, statuses, attachments', done: false },
                  { feature: 'Show → Money (beyond guarantees)', status: 'Only guarantees flow — need bonuses, commissions', done: false },
                  { feature: 'Ticket history log', status: 'Not built', done: false },
                  { feature: 'Intelligence layer', status: 'Not built', done: false },
                  { feature: 'GA4 (Google Analytics)', status: 'Not built (custom analytics exists)', done: false },
                  { feature: 'Admin dashboard UI', status: 'Not built (backend endpoints exist)', done: false },
                  { feature: 'Staging environment', status: 'Not built', done: false },
                  { feature: 'System monitoring', status: 'Not built', done: false },
                ].map((row, i) => (
                  <tr key={i}>
                    <td className="px-5 py-3 text-zinc-300">{row.feature}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[11px] font-medium ${
                        row.done ? 'text-emerald-400' :
                        row.status.includes('Not built') ? 'text-rose-400' :
                        'text-amber-400'
                      }`}>
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/[0.06] mt-16">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <span className="text-[11px] text-zinc-600">Confidential — Internal Use Only</span>
          <span className="text-[11px] text-zinc-700">Audited: March 8, 2026 (frontend + backend-dizee)</span>
        </div>
      </footer>
    </div>
  )
}
